import { scheduleTemplate } from "../data/scheduleTemplate";
import { PHASES } from "../data/constants";

// "Today" for schedule risk analysis = the real current date (was a mid-2025 demo anchor)
export const TODAY = new Date();

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d;
};
export const fmtDate = (d) =>
  `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;

// Phase start offset (days from project start) — the 7 phase header rows of the Gantt template (0/16/43/51/67/89/131)
const PHASE_ROWS = scheduleTemplate.filter((t) => t.isPhase).sort((a, b) => a.offset - b.offset);
export const PHASE_OFFSET = PHASES.map((_, idx) => PHASE_ROWS[idx]?.offset ?? 0);
export const PROJECT_SPAN = Math.max(...scheduleTemplate.map((t) => t.offset + t.duration)); // ~140

// Standard duration (days) of each phase, derived from the Gantt template → 16/27/8/16/22/42/9
export const PHASE_DURATION = PHASE_OFFSET.map((off, i) => {
  const next = i < PHASE_OFFSET.length - 1 ? PHASE_OFFSET[i + 1] : PROJECT_SPAN;
  return Math.max(1, next - off);
});

// Average days-in-phase vs standard, per construction phase (current-phase snapshot)
export function phaseDurationStats(projects) {
  const buckets = PHASES.map((ph, i) => ({ i, standard: PHASE_DURATION[i], total: 0, count: 0, over: 0 }));
  projects.forEach((p) => {
    const tm = phaseTiming(p);
    if (!tm) return;
    const b = buckets[p.phaseIndex];
    if (b) { b.total += tm.stayed; b.count += 1; if (tm.status === "over") b.over += 1; }
  });
  return buckets.map((b) => ({
    phaseIndex: b.i, standard: b.standard, count: b.count, overCount: b.over,
    avgStayed: b.count ? Math.round(b.total / b.count) : 0,
  }));
}

// Per-project-type standard durations (超期口径), loaded from GET /phase-standards at app start
const TYPE_ALIAS = { "加建": "addition", Addition: "addition", "新建": "new_build", "New Build": "new_build", Remodel: "remodel", "改造": "remodel", adu: "ADU" };
export const canonType = (t) => TYPE_ALIAS[(t || "").trim()] || (t || "").trim();
let STANDARDS = { default: PHASE_DURATION, byType: {} };
export function setPhaseStandards(cfg) {
  if (!cfg) return;
  STANDARDS = { default: Array.isArray(cfg.default) && cfg.default.length === 7 ? cfg.default : PHASE_DURATION, byType: cfg.byType || {} };
}
export const durationsFor = (type) => STANDARDS.byType[canonType(type)] || STANDARDS.default;
export const offsetsFor = (durations) => { const off = [0]; for (let i = 1; i < 7; i++) off.push(off[i - 1] + durations[i - 1]); return off; };

// Timing of the project's CURRENT phase: start date, days stayed, standard duration, deviation, status
export function phaseTiming(project, today = TODAY) {
  const idx = project.phaseIndex;
  if (idx == null || idx < 0 || idx >= 7 || !project.startDate) return null;
  const dur = durationsFor(project.type);
  const phaseStart = addDays(project.startDate, offsetsFor(dur)[idx]);
  if (Number.isNaN(phaseStart.getTime())) return null;
  const stayed = Math.max(0, Math.floor((today - phaseStart) / 86400000));
  const standard = dur[idx];
  const deviation = stayed - standard;
  let status = "normal";
  if (deviation > standard) status = "over";
  else if (deviation > 0) status = "warn";
  return { phaseStart, stayed, standard, deviation, status };
}

// Build a project's concrete schedule from its startDate
export function buildSchedule(project) {
  return scheduleTemplate.map((task) => {
    const start = addDays(project.startDate, task.offset);
    const end = addDays(project.startDate, task.offset + task.duration);
    let status;
    if (task.isPhase) status = "phase";
    else if (project.phaseIndex > task.phase) status = "done";
    else if (project.phaseIndex === task.phase) status = "active";
    else status = "upcoming";
    return { ...task, start, end };
  }).map((t) => ({
    ...t,
    status: t.isPhase ? "phase" : (project.phaseIndex > t.phase ? "done" : project.phaseIndex === t.phase ? "active" : "upcoming"),
  }));
}

export const expectedPhaseByDate = (project, today = TODAY) => {
  const elapsed = Math.floor((today - new Date(project.startDate)) / 86400000);
  let ph = 0;
  PHASE_OFFSET.forEach((off, i) => { if (elapsed >= off) ph = i; });
  return { elapsed, expected: ph };
};

// "AI" risk assessment (heuristic on schedule vs actual progress)
export function assessRisk(project, today = TODAY) {
  if (project.phaseIndex < 0) {
    return { project, level: "low", zh: "设计阶段，尚未进入施工", en: "Design stage — not yet in construction", elapsed: 0, expected: 0, gap: 0, overrun: 0 };
  }
  if (project.phaseIndex >= 7) {
    return { project, level: "low", zh: "项目已完成，通过市政建筑终检", en: "Completed — passed final city inspection", elapsed: 0, expected: 7, gap: 0, overrun: 0 };
  }
  const { elapsed, expected } = expectedPhaseByDate(project, today);
  const gap = expected - project.phaseIndex; // positive => behind schedule
  const overrun = elapsed - PROJECT_SPAN; // days past planned total
  let level = "low";
  let zh = "\u8fdb\u5ea6\u6b63\u5e38\uff0c\u6309\u8ba1\u5212\u63a8\u8fdb";
  let en = "On track with the schedule";
  if (project.phaseIndex < 6 && overrun > 20) {
    level = "high";
    zh = `\u4e25\u91cd\u8d85\u671f\uff1a\u5df2\u8d85\u8ba1\u5212\u5de5\u671f ${overrun} \u5929\uff0c\u4ecd\u5904\u4e8e\u201c${PHASES[project.phaseIndex].zh}\u201d\u9636\u6bb5`;
    en = `Severely overdue: ${overrun} days past plan, still at "${PHASES[project.phaseIndex].en}"`;
  } else if (gap >= 2) {
    level = "high";
    zh = `\u8fdb\u5ea6\u505c\u6ede\uff1a\u5e94\u5230\u201c${PHASES[expected].zh}\u201d\uff0c\u5b9e\u9645\u4ecd\u5728\u201c${PHASES[project.phaseIndex].zh}\u201d\uff0c\u843d\u540e ${gap} \u4e2a\u9636\u6bb5`;
    en = `Stalled: should be at "${PHASES[expected].en}" but still at "${PHASES[project.phaseIndex].en}" (${gap} phases behind)`;
  } else if (gap === 1) {
    level = "medium";
    zh = `\u7565\u6709\u6ede\u540e\uff1a\u843d\u540e\u8ba1\u5212 1 \u4e2a\u9636\u6bb5\uff0c\u9700\u5173\u6ce8`;
    en = `Slightly behind: 1 phase behind plan, monitor closely`;
  }
  return { project, level, zh, en, elapsed, expected, gap, overrun };
}

export function riskSummary(projects, today = TODAY) {
  const all = projects.map((p) => assessRisk(p, today));
  const high = all.filter((r) => r.level === "high");
  const medium = all.filter((r) => r.level === "medium");
  return { all, high, medium, low: all.filter((r) => r.level === "low") };
}
